/**
 * Layout step — arrange widgets on the 12-column grid.
 *
 * Widgets are absolutely positioned over a measured canvas so dragging and
 * resizing can be expressed in grid cells rather than pixels. Pointer events
 * (not mouse events) are used so the same code works with touch and pen.
 *
 * PERFORMANCE: the in-progress geometry is held in local `draft` state and
 * committed to the dashboard config only on pointer-up. Committing on every
 * pointermove would re-render every widget's content 60 times a second.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, LayoutGrid, Move, Trash2 } from 'lucide-react';
import { useContainerSize } from './components/DashboardGrid';
import WidgetRenderer from './components/WidgetRenderer';
import { createId } from '../services/storage';
import {
  GRID_COLUMNS,
  GRID_GAP,
  GRID_ROW_HEIGHT,
  clampLayout,
  fitRowHeight,
  nextFreeRow,
  packWidgets,
  resolveCollisions,
  rowsFitInHeight,
} from './utils/dashboardUtils';
import type { DashboardConfig, DashboardWidget, WidgetLayout } from './types/dashboardTypes';

interface Props {
  config: DashboardConfig;
  onChange: (config: DashboardConfig) => void;
  selectedWidgetId: string | null;
  onSelectWidget: (id: string | null) => void;
}

/**
 * Height of the published page as a fraction of its width.
 *
 * A dashboard fills the browser below a slim header, so a 16:9-ish window
 * leaves roughly half its width in height. Keeping the builder canvas at this
 * ratio means the arrangement translates rather than reflowing on publish.
 */
const PAGE_ASPECT = 0.5;

interface DragState {
  widgetId: string;
  mode: 'move' | 'resize';
  pointerStart: { x: number; y: number };
  layoutStart: WidgetLayout;
}

export default function DashboardLayout({
  config,
  onChange,
  selectedWidgetId,
  onSelectWidget,
}: Props) {
  const { ref, width } = useContainerSize<HTMLDivElement>();
  const [drag, setDrag] = useState<DragState | null>(null);
  const [draft, setDraft] = useState<WidgetLayout | null>(null);

  // Read inside global pointer handlers, which are bound once per drag.
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  const draftRef = useRef<WidgetLayout | null>(null);
  draftRef.current = draft;

  const cellWidth = width > 0 ? (width - GRID_GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS : 0;

  /* The canvas is a scale model of the published page, so what the admin
     arranges here is what an end user gets. Its height comes from its width
     via the page's aspect ratio rather than from a fixed row height — that is
     what makes the builder agree with the now-unscrollable viewer. */
  const rows = Math.max(1, nextFreeRow(config.widgets));
  const canvasHeight = width > 0 ? width * PAGE_ASPECT : rows * GRID_ROW_HEIGHT;
  const rowHeight = fitRowHeight(canvasHeight, rows);
  const fits = rowsFitInHeight(canvasHeight, rows);

  const toPixels = (layout: WidgetLayout) => ({
    left: layout.x * (cellWidth + GRID_GAP),
    top: layout.y * (rowHeight + GRID_GAP),
    width: layout.w * cellWidth + (layout.w - 1) * GRID_GAP,
    height: layout.h * rowHeight + (layout.h - 1) * GRID_GAP,
  });

  const commit = useCallback(
    (widgetId: string, layout: WidgetLayout) => {
      const moved = config.widgets.map((widget) =>
        widget.id === widgetId ? { ...widget, layout: clampLayout(layout) } : widget,
      );
      // The dropped widget keeps the cell it was released on; anything it now
      // overlaps is pushed down, so widgets can never render on top of each other.
      onChange({ ...config, widgets: resolveCollisions(moved, widgetId) });
    },
    [config, onChange],
  );

  // Bound on window so a fast drag that leaves the canvas still tracks.
  useEffect(() => {
    if (!drag) return undefined;

    const handleMove = (event: PointerEvent) => {
      const active = dragRef.current;
      if (!active || cellWidth <= 0) return;

      const deltaX = event.clientX - active.pointerStart.x;
      const deltaY = event.clientY - active.pointerStart.y;
      const cellsX = Math.round(deltaX / (cellWidth + GRID_GAP));
      const cellsY = Math.round(deltaY / (rowHeight + GRID_GAP));

      const next =
        active.mode === 'move'
          ? {
              ...active.layoutStart,
              x: active.layoutStart.x + cellsX,
              y: active.layoutStart.y + cellsY,
            }
          : {
              ...active.layoutStart,
              w: active.layoutStart.w + cellsX,
              h: active.layoutStart.h + cellsY,
            };

      setDraft(clampLayout(next));
    };

    const handleUp = () => {
      const active = dragRef.current;
      const finalLayout = draftRef.current;
      if (active && finalLayout) commit(active.widgetId, finalLayout);
      setDrag(null);
      setDraft(null);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
    // `rowHeight` belongs here too: it converts pointer travel into grid rows,
    // and a stale value would make the drag snap to the wrong row after the
    // canvas resizes.
  }, [drag, cellWidth, rowHeight, commit]);

  const startDrag = (
    event: React.PointerEvent,
    widget: DashboardWidget,
    mode: 'move' | 'resize',
  ) => {
    event.preventDefault();
    event.stopPropagation();
    onSelectWidget(widget.id);
    setDrag({
      widgetId: widget.id,
      mode,
      pointerStart: { x: event.clientX, y: event.clientY },
      layoutStart: widget.layout,
    });
    setDraft(widget.layout);
  };

  const removeWidget = (id: string) => {
    onChange({ ...config, widgets: config.widgets.filter((widget) => widget.id !== id) });
    if (selectedWidgetId === id) onSelectWidget(null);
  };

  const duplicateWidget = (widget: DashboardWidget) => {
    const copy: DashboardWidget = {
      ...widget,
      id: createId('wgt'),
      title: `${widget.title} (copy)`,
      filters: widget.filters.map((clause) => ({ ...clause, id: createId('flt') })),
      config: { ...widget.config },
      layout: { ...widget.layout, y: widget.layout.y + widget.layout.h },
    };
    onChange({
      ...config,
      widgets: resolveCollisions([...config.widgets, copy], copy.id),
    });
    onSelectWidget(copy.id);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Layout</h2>
          <p className="text-xs text-slate-500">
            Drag the handle to move a widget; drag the bottom-right corner to resize.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              onChange({ ...config, widgets: packWidgets(config.widgets, { stretch: true }) })
            }
            disabled={config.widgets.length === 0}
            className="gl-btn gl-btn--secondary px-2.5 py-1.5 text-xs disabled:opacity-40"
            title="Reflow every widget into full, evenly-sized rows"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Auto arrange
          </button>
          <span className="text-[11px] text-slate-400">{GRID_COLUMNS}-column grid</span>
        </div>
      </div>

      {!fits && (
        <p
          role="alert"
          className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800"
        >
          These widgets need more rows than fit on one screen, so tiles are at their minimum
          height and the published dashboard will be cramped. Reduce widget heights, or put
          fewer of them on this dashboard.
        </p>
      )}

      {config.widgets.length === 0 ? (
        <div className="gl-panel border-dashed py-16 text-center">
          <p className="text-sm text-slate-500">No widgets to arrange</p>
          <p className="text-xs text-slate-400">Add widgets first.</p>
        </div>
      ) : (
        <div
          ref={ref}
          // Mirrors the published page, dark theme included.
          data-theme={config.theme.mode === 'dark' ? 'dark' : undefined}
          className="relative rounded-xl bg-slate-100 p-0"
          style={{
            height: canvasHeight,
            // Grid guides, so the snap targets are visible while dragging.
            backgroundImage:
              cellWidth > 0
                ? `repeating-linear-gradient(to right, rgba(148,163,184,0.25) 0 1px, transparent 1px ${
                    cellWidth + GRID_GAP
                  }px), repeating-linear-gradient(to bottom, rgba(148,163,184,0.25) 0 1px, transparent 1px ${
                    rowHeight + GRID_GAP
                  }px)`
                : undefined,
            touchAction: drag ? 'none' : undefined,
          }}
        >
          {config.widgets.map((widget) => {
            const isDragging = drag?.widgetId === widget.id;
            const layout = isDragging && draft ? draft : widget.layout;
            const box = toPixels(layout);
            const selected = widget.id === selectedWidgetId;

            return (
              <div
                key={widget.id}
                onClick={() => onSelectWidget(widget.id)}
                className={`absolute transition-shadow ${
                  isDragging ? 'z-30 opacity-90 shadow-2xl' : 'z-10'
                }`}
                style={{
                  left: box.left,
                  top: box.top,
                  width: box.width,
                  height: box.height,
                  outline: selected ? '2px solid #2563EB' : undefined,
                  outlineOffset: 2,
                  borderRadius: 12,
                }}
              >
                <div className="h-full w-full overflow-hidden rounded-xl">
                  <WidgetRenderer
                    widget={widget}
                    dataSources={config.dataSources}
                    refreshInterval={0}
                    accentColor={config.theme.accentColor}
                    themeMode={config.theme.mode}
                    resizeToken={`${layout.w}|${layout.h}|${Math.round(cellWidth)}|${Math.round(rowHeight)}`}
                  />
                </div>

                {/* Controls sit above the widget so its own header stays usable. */}
                <div className="absolute right-1 top-1 z-20 flex gap-0.5 rounded-lg bg-white/95 p-0.5 shadow-sm ring-1 ring-slate-200">
                  <button
                    type="button"
                    onPointerDown={(event) => startDrag(event, widget, 'move')}
                    aria-label={`Move ${widget.title}`}
                    title="Drag to move"
                    className="cursor-move rounded p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                  >
                    <Move className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => duplicateWidget(widget)}
                    aria-label={`Duplicate ${widget.title}`}
                    title="Duplicate"
                    className="rounded p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeWidget(widget.id)}
                    aria-label={`Delete ${widget.title}`}
                    title="Delete"
                    className="rounded p-1 text-slate-500 transition hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div
                  onPointerDown={(event) => startDrag(event, widget, 'resize')}
                  role="slider"
                  tabIndex={0}
                  aria-label={`Resize ${widget.title}`}
                  aria-valuenow={layout.w}
                  aria-valuemin={1}
                  aria-valuemax={GRID_COLUMNS}
                  onKeyDown={(event) => {
                    // Keyboard resizing, so the grid is not pointer-only.
                    const step = event.shiftKey ? { w: 0, h: 1 } : { w: 1, h: 0 };
                    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                      commit(widget.id, {
                        ...widget.layout,
                        w: widget.layout.w + step.w,
                        h: widget.layout.h + step.h,
                      });
                    }
                    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                      commit(widget.id, {
                        ...widget.layout,
                        w: widget.layout.w - step.w,
                        h: widget.layout.h - step.h,
                      });
                    }
                  }}
                  className="absolute -bottom-0.5 -right-0.5 z-20 h-4 w-4 cursor-se-resize rounded-br-xl rounded-tl-md bg-white shadow ring-1 ring-slate-300"
                  style={{ touchAction: 'none' }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
